'use client';

import { createContext, useContext, useState, useCallback } from 'react';
import type { Organisation } from '@/types/organisation';
import type { Job } from '@/types/jobs';
import type { Candidate } from '@/types/search';
import type { PublicCandidateAuthState } from '@/types/public';
import { publicApi } from '@/api/public';
import { useToast } from '@/hooks/use-toast';
import { determineAuthState, extractCandidate } from '@/utils/publicAuthUtils';

interface PublicState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

interface PaginatedJobs extends PublicState<Job[]> {
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
    limit: number;
  } | null;
}

interface PublicContextType {
  organisation: PublicState<Organisation>;
  jobs: PaginatedJobs;
  job: PublicState<Job>;
  candidate: PublicState<Candidate>;
  candidateAuthState: PublicCandidateAuthState | null;
  fetchOrganisation: (orgId: string) => Promise<void>;
  fetchJobs: (orgId: string, page?: number) => Promise<void>;
  fetchJob: (orgId: string, jobId: string) => Promise<Job | void>;
  fetchCandidateByCode: (candidateCode: string, jobId?: string) => Promise<Candidate | void>;
}

const PublicContext = createContext<PublicContextType | undefined>(undefined);

export function PublicProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();

  const [organisation, setOrganisation] = useState<PublicState<Organisation>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [jobs, setJobs] = useState<PaginatedJobs>({
    data: null,
    pagination: null,
    isLoading: false,
    error: null,
  });

  const [job, setJob] = useState<PublicState<Job>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [candidate, setCandidate] = useState<PublicState<Candidate>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [candidateAuthState, setCandidateAuthState] = useState<PublicCandidateAuthState | null>(null);

  const fetchOrganisation = useCallback(
    async (orgId: string) => {
      setOrganisation(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const { organisation: data } = await publicApi.getOrganisation(orgId);
        setOrganisation({
          data,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to fetch organisation';
        setOrganisation(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'error',
        });
      }
    },
    [toast]
  );

  const fetchJobs = useCallback(
    async (orgId: string, page = 1) => {
      setJobs(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await publicApi.listJobs(orgId, { page });
        setJobs({
          data: response.jobs,
          pagination: response.pagination,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch jobs';
        setJobs(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'error',
        });
      }
    },
    [toast]
  );

  const fetchJob = useCallback(
    async (orgId: string, jobId: string) => {
      setJob(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const { job: data } = await publicApi.getJob(orgId, jobId);
        setJob({
          data,
          isLoading: false,
          error: null,
        });
        return data;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch job details';
        setJob(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const fetchCandidateByCode = useCallback(async (candidateCode: string, jobId?: string) => {
    setCandidate(prev => ({ ...prev, isLoading: true, error: null }));
    setCandidateAuthState({ type: 'LOADING' });
    
    try {
      // Use auth-aware API method
      const response = await publicApi.getCandidateByCodeWithAuth(candidateCode, jobId);
      const authState = determineAuthState(response);
      setCandidateAuthState(authState);
      
      // Extract candidate if available
      const candidateData = extractCandidate(authState);
      if (candidateData) {
        setCandidate({
          data: candidateData,
          isLoading: false,
          error: null,
        });
        return candidateData;
      } else {
        // Auth required or error state
        setCandidate({
          data: null,
          isLoading: false,
          error: authState.type === 'AUTH_REQUIRED' 
            ? 'Authentication required' 
            : authState.type === 'ACCESS_DENIED'
            ? authState.message
            : authState.type === 'NOT_FOUND'
            ? authState.message
            : authState.type === 'ERROR'
            ? authState.error
            : 'Failed to fetch candidate',
        });
        return undefined;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch candidate details';
      setCandidate(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      setCandidateAuthState({
        type: 'ERROR',
        error: errorMessage,
      });
    }
  }, []);

  return (
    <PublicContext.Provider
      value={{
        organisation,
        jobs,
        job,
        candidate,
        candidateAuthState,
        fetchOrganisation,
        fetchJobs,
        fetchJob,
        fetchCandidateByCode,
      }}
    >
      {children}
    </PublicContext.Provider>
  );
}

export function usePublic() {
  const context = useContext(PublicContext);
  if (context === undefined) {
    throw new Error('usePublic must be used within a PublicProvider');
  }
  return context;
}
